<template>
  <div ref="menu" class="user-menu">
    <button v-if="!authStore.isAuthenticated" class="button primary" type="button" @click="login">
      <LogIn :size="18" />登录
    </button>
    <template v-else>
      <button
        class="user-trigger"
        type="button"
        :aria-expanded="open"
        aria-haspopup="menu"
        aria-label="打开用户菜单"
        @click.stop="open = !open"
      >
        <img
          v-if="avatarRequest"
          v-show="avatarRequest.status === 'loaded'"
          :key="avatarRequest.id"
          :src="avatarRequest.src"
          alt=""
          @load="avatarRequest.onLoad"
          @error="avatarRequest.onError"
        />
        <span v-if="avatarRequest?.status !== 'loaded'" class="avatar-fallback">{{ initial }}</span>
        <span v-if="!compact" class="user-trigger-name">{{ userName }}</span>
        <ChevronDown :size="16" :class="{ rotated: open }" aria-hidden="true" />
      </button>

      <Transition name="menu-fade">
        <div v-if="open" class="user-popover" role="menu">
          <div class="user-popover-header">
            <strong>{{ userName }}</strong>
            <span>{{ authStore.userInfo?.email }}</span>
            <small v-if="roleLabel">{{ roleLabel }}</small>
          </div>
          <RouterLink role="menuitem" :to="{ name: 'ManagementOverview' }" @click="open = false">
            <LayoutDashboard :size="18" />管理平台
          </RouterLink>
          <RouterLink role="menuitem" :to="{ name: 'MicroseismicEvents' }" @click="open = false">
            <Activity :size="18" />微震事件
          </RouterLink>
          <RouterLink role="menuitem" :to="{ name: 'EnvelopePending' }" @click="open = false">
            <TriangleAlert :size="18" />异常待确认
          </RouterLink>
          <RouterLink role="menuitem" :to="{ name: 'EnvelopeHistory' }" @click="open = false">
            <History :size="18" />历史预警
          </RouterLink>
          <RouterLink role="menuitem" :to="{ name: 'DashboardGrid', query: { mode: 'history' } }" @click="open = false">
            <History :size="18" />微震历史分析
          </RouterLink>
          <a role="menuitem" href="/api/auth/management/enter/?target=account&amp;return_path=%2Fsettings%2Fprofile" target="_blank" rel="noopener">
            <UserRound :size="18" />账号资料
          </a>
          <div class="menu-divider"></div>
          <p v-if="logoutError" role="status">{{ logoutError }}</p>
          <button role="menuitem" type="button" class="logout-item" :disabled="loggingOut" @click="logout">
            <LogOut :size="18" />退出登录
          </button>
        </div>
      </Transition>
    </template>
  </div>
</template>

<script>
import { RouterLink } from 'vue-router'
import {
  Activity,
  ChevronDown,
  History,
  LayoutDashboard,
  LogIn,
  LogOut,
  TriangleAlert,
  UserRound
} from '@lucide/vue'
import { useAuthStore } from '@/store/auth'
import { buildSsoLoginUrl } from '@/utils/sso'

export default {
  name: 'UserMenu',
  components: {
    Activity,
    RouterLink,
    ChevronDown,
    History,
    LayoutDashboard,
    LogIn,
    LogOut,
    TriangleAlert,
    UserRound
  },
  props: {
    compact: { type: Boolean, default: false }
  },
  setup() {
    return { authStore: useAuthStore() }
  },
  data() {
    return {
      open: false,
      loggingOut: false,
      logoutError: '',
      avatarRequest: null,
      avatarSerial: 0,
      avatarRetryTimer: null,
      avatarDisposed: false
    }
  },
  computed: {
    userName() {
      return this.authStore.userInfo?.username || this.authStore.userInfo?.email || '用户'
    },
    avatar() {
      return this.authStore.userInfo?.avatar || ''
    },
    avatarAccount() {
      return this.authStore.userInfo?.email || ''
    },
    avatarIdentity() {
      return JSON.stringify([this.authStore.isAuthenticated, this.avatarAccount, this.avatar])
    },
    initial() {
      return this.userName.charAt(0).toUpperCase()
    },
    roleLabel() {
      const role = this.authStore.userInfo?.role
      if (role === 'superuser') return '超级管理员'
      if (role === 'admin') return '平台管理员'
      return ''
    }
  },
  watch: {
    avatarIdentity: {
      immediate: true,
      handler() {
        this.clearAvatarRetry()
        this.avatarRequest = null
        if (this.authStore.isAuthenticated && this.avatar) this.startAvatarRequest(this.avatar)
      }
    },
    avatarAccount() {
      this.open = false
    }
  },
  created() {
    if (this.authStore.isAuthenticated && !this.authStore.userInfo) {
      this.authStore.fetchUserInfo().catch(() => {})
    }
    document.addEventListener('click', this.closeOutside)
  },
  beforeUnmount() {
    this.avatarDisposed = true
    this.clearAvatarRetry()
    document.removeEventListener('click', this.closeOutside)
  },
  methods: {
    clearAvatarRetry() {
      if (this.avatarRetryTimer !== null) clearTimeout(this.avatarRetryTimer)
      this.avatarRetryTimer = null
    },
    startAvatarRequest(src, retried = false) {
      const id = ++this.avatarSerial
      const identity = this.avatarIdentity
      // 每个 img 保存自己的回调；账号切换、URL 更新和重试均使用独立节点。
      const isCurrent = () => !this.avatarDisposed && this.avatarIdentity === identity && this.avatarRequest?.id === id
      this.avatarRequest = {
        id,
        src,
        status: 'loading',
        onLoad: () => {
          if (isCurrent() && this.avatarRequest.status === 'loading') this.avatarRequest.status = 'loaded'
        },
        onError: () => {
          if (!isCurrent() || this.avatarRequest.status !== 'loading') return
          this.avatarRequest.status = 'failed'
          if (retried) return
          this.avatarRetryTimer = setTimeout(() => {
            if (!isCurrent()) return
            this.avatarRetryTimer = null
            // 只追加重试参数，保留账号中心地址的原有编码、查询参数与片段。
            const hashIndex = src.indexOf('#')
            const base = hashIndex < 0 ? src : src.slice(0, hashIndex)
            const hash = hashIndex < 0 ? '' : src.slice(hashIndex)
            const separator = base.includes('?') ? '&' : '?'
            this.startAvatarRequest(`${base}${separator}_microquake_avatar_retry=${Date.now()}-${id}${hash}`, true)
          }, 2000)
        }
      }
    },
    closeOutside(event) {
      if (!this.$refs.menu?.contains(event.target)) this.open = false
    },
    login() {
      window.location.href = buildSsoLoginUrl()
    },
    async logout() {
      this.loggingOut = true
      this.logoutError = ''
      try {
        await this.authStore.logout()
      } catch {
        this.logoutError = '退出未完成，请检查网络后重试。'
      } finally {
        this.loggingOut = false
      }
    }
  }
}
</script>
